import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Table from './subcomponents/Table'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Advertisers/List/filters'
import { Button, Grid, Icon } from 'gu-ui-kit'
import DropdownSortingMenu from 'components/DropdownSortingMenu'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { DROP_LIST, GET_LIST, SET_FILTERS } from 'modules/Advertisers/List/types'
import { isEmpty } from 'lodash'
import GridSpinner from 'components/UI/GridSpinner'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/no-referrals.svg'
import PageHeader from 'components/PageHeader'

const AdvertisersList = () => {
  const [page, setPage] = useState(1)
  const dispatch = useDispatch()
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()

  const [sorting, setSorting] = useState({
    prop: 'created_at',
    direction: 'desc',
  })

  const { data, loading, pagination, i18n } = useSelector((state) => ({
    data: state['advertisers-list'].data,
    loading: state['advertisers-list'].loading,
    pagination: state['advertisers-list'].pagination,
    i18n: state['app'].i18n,
  }))

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['advertisers-list'].filters),
    config: config(),
    setAction: SET_FILTERS,
    localStorageKey: 'advertisers-list',
  })

  useEffect(() => {
    dispatch({
      type: DROP_LIST,
    })
    setPage(1)
  }, [filterManager.params, sorting])

  useEffect(() => {
    dispatch({
      type: GET_LIST,
      payload: {
        params: {
          ...filterManager.params,
          offset: pagination.limit * (page - 1),
          limit: pagination.limit,
          sort: sorting.prop,
          direction: sorting.direction,
        },
      },
    })
  }, [filterManager.params, page, sorting])

  const filteredData = useMemo(() => {
    const { status = null } = filterManager.params || {}
    return status ? data.filter((item) => item.status === status) : data
  }, [filterManager.params, data])

  return (
    <>
      <PageHeader className={'margin-bottom-24'} color={'Label-Primary'}>
        {t('advertisers::heading')}
      </PageHeader>
      {isEmpty(filterManager.params) && loading ? (
        <GridSpinner />
      ) : isEmpty(filterManager.params) && !loading && !filteredData.length ? (
        <EmptyResults
          header={t('advertisers::empty_list_heading')}
          text={t('advertisers::create')}
          actionButton={
            <Button
              variant={'primary'}
              size={'l'}
              component={Link}
              fullWidth={breakpoint.fbs}
              to={pathGen(ROUTES.ADVERTISER_NEW)}
              startIcon={<Icon name={'plus'} />}
            >
              {t('advertisers::buttons.add')}
            </Button>
          }
          image={<EmptyResultsSVG />}
        />
      ) : (
        <>
          <Grid className={'margin-bottom-64'}>
            <Button
              variant={'primary'}
              size={'l'}
              component={Link}
              to={pathGen(ROUTES.ADVERTISER_NEW)}
              startIcon={<Icon name={'plus'} />}
              fullWidth={breakpoint.fbs}
            >
              {t('advertisers::buttons.add')}
            </Button>
          </Grid>

          <FilterPanel className={'margin-bottom-32'} filterManager={filterManager} />

          <Grid className={'margin-bottom-16'}>
            <DropdownSortingMenu
              sortingRuleMap={{
                created_at: {
                  asc: 'created_at.asc',
                  desc: 'created_at.desc',
                },
                name: {
                  asc: 'name.asc',
                  desc: 'name.desc',
                },
              }}
              i18nKey={'advertisers::filters.sortmenu'}
              id={'advertisers-list-sort-menu'}
              sorting={sorting}
              onSortChange={setSorting}
            />
          </Grid>
          <Table
            i18n={i18n}
            data={filteredData}
            loading={loading}
            pagination={pagination}
            onChangePage={(val) => setPage(page + val)}
          />
        </>
      )}
    </>
  )
}

export default AdvertisersList
